import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import { t, type IntlService } from 'ember-intl';
import { htmlSafe } from '@ember/template';
import Chip from 'tio-ui/components/chip';
import { formatShortDate } from 'tio-ui/format';
import {
  PhSparkle,
  PhArrowRight,
  PhCaretDown,
  PhCaretUp,
  PhRocket,
  PhArchive,
} from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/tas/state-pill';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { isActiveInstanceState } from 'tio-common/utils/tuition-assistance/application-states';
import type {
  DataSourceInfo,
  EligibleTemplate,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';

interface TasProgramsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
  eligibleTemplates: EligibleTemplate[];
}

interface TasProgramsSignature {
  Args: {
    model: TasProgramsModel;
  };
}

export default class CareerPathwaysTasProgramsIndex extends Component<TasProgramsSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked showHistory = false;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get allPrograms(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  get allowedTypes(): Set<string> {
    return new Set(['TAS.ProgramType.1', 'TAS.ProgramType.4.2', 'TAS.ProgramType.4.3']);
  }

  get programs(): LiveTasProgram[] {
    return this.allPrograms.filter(
      (p) =>
        isActiveInstanceState(p.state) &&
        (p.typeClassification ? this.allowedTypes.has(p.typeClassification) : false)
    );
  }

  get historyPrograms(): LiveTasProgram[] {
    return this.allPrograms.filter(
      (p) =>
        !isActiveInstanceState(p.state) &&
        (p.typeClassification ? this.allowedTypes.has(p.typeClassification) : false)
    );
  }

  get hasActive() {
    return this.programs.length > 0;
  }

  get hasHistory() {
    return this.historyPrograms.length > 0;
  }

  toggleHistory = () => {
    this.showHistory = !this.showHistory;
  };

  get eligibleTemplates(): EligibleTemplate[] {
    return this.args.model.eligibleTemplates ?? [];
  }

  <template>
    <VStack class="gap-6">
      <PageHero @gradient="cool" @rounded="2xl" class="tio-elevation-card-raised">
        <:default>
          <div class="tio-col-loose md:flex-row md:items-center md:justify-between">
            <div class="max-w-2xl">
              <div
                class="tio-row-tight tio-text-xs-semibold uppercase tracking-wide tio-tas-subpage-hero-muted"
              >
                <PhSparkle class="tio-icon-sm" />
                {{t "tuition_assistance.reimagined.programs.hero_eyebrow"}}
              </div>
              <Heading @level={{1}} class="tio-fg-inverse">
                {{t "tuition_assistance.reimagined.programs.hero_title"}}
              </Heading>
              <Text class="tio-tas-subpage-hero-muted">
                {{t "tuition_assistance.reimagined.programs.hero_subtitle"}}
              </Text>
            </div>
            <LinkTo @route="authenticated.tas.dashboard">
              <Button @intent="primary">{{t
                  "tuition_assistance.reimagined.programs.cta_start"
                }}</Button>
            </LinkTo>
          </div>
        </:default>
      </PageHero>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-programs">
        <Section class="mb-6">
          <:header>
            <div class="tio-row-tight">
              <PhRocket @size={{20}} class="tio-fg-info" />
              {{t "tuition_assistance.reimagined.programs.section_active"}}
              {{#if this.hasActive}}
                <span
                  class="ml-2 rounded-full tio-bg-info-soft px-2.5 py-0.5 tio-text-xs-semibold tio-fg-info-body"
                >
                  {{this.programs.length}}
                </span>
              {{/if}}
            </div>
          </:header>
          <:body>
            {{#if this.hasActive}}
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {{#each this.programs as |program|}}
                  <div class="rounded-2xl tio-bg-surface p-5 tio-hairline-card">
                    <div class="tio-row-start-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{program.name}}</Heading>
                        <Text @variant="muted">{{program.institution}}</Text>
                        {{#if program.startDate}}
                          <Text @variant="muted" class="block whitespace-nowrap">
                            {{t
                              "tuition_assistance.reimagined.programs.label_starts"
                              date=(formatShortDate program.startDate)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <StatePill @state={{program.state}} />
                    </div>
                    <div class="mt-4 tio-row-between">
                      {{#if program.major}}
                        <Text @variant="muted">
                          {{t
                            "tuition_assistance.reimagined.programs.label_major"
                            major=program.major
                          }}
                        </Text>
                      {{else}}
                        <span></span>
                      {{/if}}
                      <LinkTo
                        @route="authenticated.tas.programs.show"
                        @model={{program.id}}
                        class="tio-text-sm-semibold app-text-link"
                      >
                        {{t "tuition_assistance.reimagined.programs.link_open"}}
                        <PhArrowRight class="tio-icon-sm ml-1 inline" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div
                class="rounded-2xl border border-dashed tio-border-strong tio-bg-sunken p-8 text-center"
              >
                <PhRocket @size={{32}} class="mx-auto mb-3 tio-fg-disabled" />
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.programs.empty_title"
                  }}</Heading>
                <Text @variant="muted">
                  {{t "tuition_assistance.reimagined.programs.empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.programs.empty_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>

        {{! History Section }}
        {{#if this.hasHistory}}
          <Section>
            <:header>
              <button
                type="button"
                class="tio-row-tight w-full text-left"
                {{on "click" this.toggleHistory}}
              >
                <PhArchive @size={{20}} class="tio-fg-muted" />
                <span>
                  {{t "tuition_assistance.reimagined.programs_index.section_history"}}
                </span>
                <span
                  class="ml-2 rounded-full tio-bg-sunken px-2.5 py-0.5 tio-text-xs-semibold tio-fg-secondary"
                >
                  {{this.historyPrograms.length}}
                </span>
                <span class="ml-auto">
                  {{#if this.showHistory}}
                    <PhCaretUp @size={{16}} class="tio-fg-muted" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="tio-fg-muted" />
                  {{/if}}
                </span>
              </button>
            </:header>
            <:body>
              {{#if this.showHistory}}
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {{#each this.historyPrograms as |program|}}
                    <div class="tio-tas-history-card">
                      <div class="tio-row-start-between gap-4">
                        <div>
                          <Heading @level={{3}}>{{program.name}}</Heading>
                          <Text @variant="muted">{{program.institution}}</Text>
                          {{#if program.startDate}}
                            <Text @variant="muted" class="block whitespace-nowrap">
                              {{t
                                "tuition_assistance.reimagined.programs.label_starts"
                                date=(formatShortDate program.startDate)
                              }}
                            </Text>
                          {{/if}}
                        </div>
                        <StatePill @state={{program.state}} />
                      </div>
                      <div class="mt-4 tio-row-between">
                        {{#if program.major}}
                          <Text @variant="muted">
                            {{t
                              "tuition_assistance.reimagined.programs.label_major"
                              major=program.major
                            }}
                          </Text>
                        {{else}}
                          <span></span>
                        {{/if}}
                        <LinkTo
                          @route="authenticated.tas.programs.show"
                          @model={{program.id}}
                          class="tio-inline-row gap-1 tio-text-sm-semibold tio-fg-secondary hover:tio-fg-primary"
                        >
                          {{t "tuition_assistance.reimagined.programs.link_open"}}
                          <PhArrowRight class="tio-icon-sm" />
                        </LinkTo>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </:body>
          </Section>
        {{/if}}
      </DataIndicator>

      {{! Eligible Benefits Section }}
      {{#if this.eligibleTemplates.length}}
        <DataIndicator
          @source={{this.tasSource}}
          @showBadge={{true}}
          @dataSourceId="tas-eligible-benefits"
        >
          <Section>
            <:header>
              {{t "tas.dashboard.eligible_benefits"}}
            </:header>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {{#each this.eligibleTemplates as |template|}}
                  <div
                    class="rounded-2xl tio-bg-surface p-5 tio-hairline-card tio-hairline-card--interactive transition-shadow tio-col-loose"
                  >
                    <div class="tio-flex-end gap-2 min-h-5">
                      {{#if template.isEnrolled}}
                        <Chip @appearance="faded" @intent="success" @size="sm">
                          {{t "tas.dashboard.chips.enrolled"}}
                        </Chip>
                      {{/if}}
                      {{#if template.isDirectPayProgram}}
                        <Chip @appearance="faded" @intent="primary" @size="sm">
                          {{htmlSafe (t "common.tas.course_cash.default")}}
                        </Chip>
                      {{/if}}
                    </div>
                    <div class="tio-col-flush gap-1.5">
                      <Heading @level={{3}}>{{template.name}}</Heading>
                      {{#if template.description}}
                        <Text @variant="muted" class="line-clamp-2">
                          {{template.description}}
                        </Text>
                      {{/if}}
                    </div>
                    <div>
                      <LinkTo
                        @route="authenticated.tas.getting-started"
                        @model={{template.id}}
                        class="tio-text-sm-semibold app-text-link"
                      >
                        {{t "tas.dashboard.see_more"}}
                        <PhArrowRight class="tio-icon-sm ml-1 inline" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            </:body>
          </Section>
        </DataIndicator>
      {{/if}}
    </VStack>
  </template>
}
